// The poster gallery's slideshow (PosterSlideshow.vue; Seán, 2026-10-26): the gallery's posters
// one after another, each whole on the screen and sharp at its resolution (OpenSeadragon on the
// old server's pyramids, as the poster page). Two viewers, one shown and one loading the next
// behind it, crossfaded once the next is loaded; a few seconds on each, or stepped by hand
import OpenSeadragon from "openseadragon";
import {getPosterFileUrl, getThumbnailUrl} from "@/composables/usePosters.js";

export const SLIDE_MS = 8000; // each poster's time on the screen
export const FADE_MS = 700; // the crossfade (PosterSlideshow.vue's CSS)
const LOAD_WAIT_MS = 4000; // the longest a slide waits to be sharp before it's shown anyway

function getSlideViewer(element) {
  const viewer = OpenSeadragon({
    element,
    drawer: "canvas", // the tiles are on another origin, without the CORS headers WebGL needs
    subPixelRoundingForTransparency: OpenSeadragon.SUBPIXEL_ROUNDING_OCCURRENCES.ALWAYS, // seams
    showNavigationControl: false,
    mouseNavEnabled: false,
    visibilityRatio: 1,
  });
  // the window resized (full screen in or out): the whole poster again, a frame later
  viewer.addHandler("after-resize", () => requestAnimationFrame(() => {
    viewer.viewport?.goHome(true);
  }));
  return viewer;
}
// its pyramid, else its large thumbnail (the few without one)
const getSource = (poster) => (poster.pyramid ? getPosterFileUrl(poster, "xml")
  : {type: "image", url: getThumbnailUrl(poster, 600)});

// a poster opened in a viewer: done once its tiles for the screen have arrived (or failed, or
// after LOAD_WAIT_MS)
function load(viewer, poster) {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, LOAD_WAIT_MS);
    const done = () => clearTimeout(timer) || resolve();
    viewer.addOnceHandler("open-failed", done);
    viewer.addOnceHandler("open", () => {
      const item = viewer.world.getItemAt(0);
      if (!item || item.getFullyLoaded()) return done();
      item.addOnceHandler("fully-loaded-change", done);
    });
    viewer.open(getSource(poster));
  });
}

// elements: the two viewers' boxes; onShow(index): a poster is on the screen. Returns the
// controls: goTo, step (±1), setPlaying, destroy
export function createSlideshow({elements, posters, onShow}) {
  const viewers = elements.map(getSlideViewer);
  const state = {front: 0, index: -1, token: 0, timer: 0, isPlaying: true, preload: null};
  const getIndex = (index) => (index + posters.length) % posters.length;
  // the next poster loading behind the shown one, after the crossfade
  function preloadNext() {
    const index = getIndex(state.index + 1);
    state.preload = {index, promise: load(viewers[1 - state.front], posters[index])};
  }
  function schedule() {
    clearTimeout(state.timer);
    if (state.isPlaying) state.timer = setTimeout(() => goTo(state.index + 1), SLIDE_MS);
  }
  function show(index) {
    const back = 1 - state.front;
    elements[back].style.opacity = "1";
    elements[state.front].style.opacity = "0";
    [state.front, state.index] = [back, index];
    onShow(index);
    schedule();
    const token = state.token; // not if another poster was asked for meanwhile
    setTimeout(() => token === state.token && preloadNext(), FADE_MS);
  }
  async function goTo(target) {
    const [index, token] = [getIndex(target), ++state.token];
    clearTimeout(state.timer);
    const isPreloaded = state.preload?.index === index;
    await (isPreloaded ? state.preload.promise : load(viewers[1 - state.front], posters[index]));
    state.preload = null;
    if (token === state.token) show(index);
  }
  function setPlaying(isOn) {
    state.isPlaying = isOn;
    if (isOn) return schedule();
    clearTimeout(state.timer);
  }
  return {
    goTo,
    step: (delta) => goTo(state.index + delta),
    setPlaying,
    destroy: () => {
      clearTimeout(state.timer);
      state.token++;
      viewers.forEach((viewer) => viewer.destroy());
    },
  };
}
